import { useState, useEffect } from 'react';
import {
    FormControl,
    FormLabel,
    InputGroup,
    InputRightElement,
    IconButton,
    Box,
    Stack,
    Text,
} from '@chakra-ui/react';
import { CloseIcon, SearchIcon } from '@chakra-ui/icons';
import { Input } from '@/components/v4';
import WholesalerPicker, { prefetchWholesalers } from './WholesalerPicker';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import {
    stackSpacing,
    formLabelStyling,
    stackStyling,
    descriptionStyling,
} from 'components/forms/new/formStyles';

/**
 * Wholesaler Field Component
 * Integrates with Formik forms
 *
 * @param {object} values - Formik values object
 * @param {function} setFieldValue - Formik setFieldValue function
 * @param {boolean} darkMode - Dark mode flag
 * @param {object} errors - Formik errors object
 * @param {object} touched - Formik touched object
 * @param {string} label - Field label (default: "Wholesaler")
 * @param {string} description - Field description
 * @param {boolean} required - Whether field is required
 * @param {string} dataWalkthroughid - Data walkthrough ID for testing
 */
const WholesalerField = ({
    values,
    setFieldValue,
    darkMode = false,
    errors = {},
    touched = {},
    label = 'Wholesaler',
    description = 'Select the wholesaler for this number block.',
    required = false,
    dataWalkthroughid = '',
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [wholesalerName, setWholesalerName] = useState('');
    const [isLoadingName, setIsLoadingName] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    // Fetch wholesaler name when ID changes - uses shared cache from WholesalerPicker
    useEffect(() => {
        const fetchWholesalerName = async () => {
            if (!values.wholesalerID) {
                setWholesalerName('');
                return;
            }

            setIsLoadingName(true);
            try {
                // Use shared cache from WholesalerPicker - no duplicate API call
                const wholesalers = await prefetchWholesalers();
                const wholesaler = wholesalers.find(
                    (w) => w.id === values.wholesalerID,
                );
                if (wholesaler) {
                    setWholesalerName(wholesaler.name);
                } else {
                    setWholesalerName('Unknown');
                }
            } catch (error) {
                console.error(
                    '[WholesalerField] Failed to fetch wholesaler name:',
                    error,
                );
                setWholesalerName('Error loading name');
            } finally {
                setIsLoadingName(false);
            }
        };

        fetchWholesalerName();
    }, [values.wholesalerID]);

    const handleSelect = ({ wholesalerId, wholesalerName: name }) => {
        // When wholesaler changes, clear partner and company selections
        setFieldValue('wholesalerID', wholesalerId);
        setFieldValue('partnerID', '');
        setFieldValue('companyID', '');
        setWholesalerName(name);
    };

    const handleClear = () => {
        setFieldValue('wholesalerID', '');
        setFieldValue('partnerID', '');
        setFieldValue('companyID', '');
        setWholesalerName('');
    };

    return (
        <>
            <FormControl
                sx={{ margin: '0 !important' }}
                isInvalid={!!errors.wholesalerID && touched.wholesalerID}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling(true)}>
                    <Box>
                        <FormLabel sx={formLabelStyling(darkMode)}>
                            {label}{' '}
                            {required && (
                                <span style={{ color: '#e53e3e' }}>*</span>
                            )}
                        </FormLabel>
                        {description && (
                            <Text sx={descriptionStyling} maxWidth={250}>
                                {description}
                            </Text>
                        )}
                    </Box>
                    <Box flex="1" minWidth={0}>
                        <Box
                            sx={{
                                display: 'flex',
                                width: '100%',
                                alignItems: 'flex-start',
                                paddingTop: '15px',
                            }}>
                            <Box width="100%">
                                <InputGroup>
                                    <Input
                                        placeholder="Not Assigned"
                                        value={
                                            isLoadingName
                                                ? 'Loading...'
                                                : wholesalerName
                                        }
                                        onClick={() => setIsModalOpen(true)}
                                        readOnly
                                        cursor="pointer"
                                        sx={{
                                            height: '30px',
                                            padding: '2px 6px',
                                            borderRadius: '5px',
                                        }}
                                        data-walkthroughid={dataWalkthroughid}
                                    />
                                    <InputRightElement
                                        width="auto"
                                        height="30px"
                                        pr={2}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}>
                                            {values.wholesalerID && (
                                                <IconButton
                                                    aria-label="Clear wholesaler"
                                                    icon={
                                                        <CloseIcon fontSize="xs" />
                                                    }
                                                    size="xs"
                                                    variant="ghost"
                                                    onClick={handleClear}
                                                    _hover={{
                                                        bg: darkMode
                                                            ? '#4A5568'
                                                            : 'gray.200',
                                                    }}
                                                />
                                            )}
                                            <IconButton
                                                aria-label="Open picker"
                                                icon={
                                                    <SearchIcon fontSize="xs" />
                                                }
                                                size="xs"
                                                variant="ghost"
                                                onClick={() =>
                                                    setIsModalOpen(true)
                                                }
                                                _hover={{
                                                    bg: darkMode
                                                        ? '#4A5568'
                                                        : 'gray.200',
                                                }}
                                            />
                                        </Box>
                                    </InputRightElement>
                                </InputGroup>
                                {errors.wholesalerID &&
                                    touched.wholesalerID && (
                                        <Text
                                            color="red.500"
                                            fontSize="sm"
                                            mt={1}>
                                            {errors.wholesalerID}
                                        </Text>
                                    )}
                            </Box>
                        </Box>
                    </Box>
                    {isHovered && (
                        <FieldToolTip
                            label="Select the wholesaler for this number block."
                            ariaLabel="wholesaler-tip"
                            left="25px"
                            top="25px"
                        />
                    )}
                </Stack>
            </FormControl>

            <WholesalerPicker
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSelect={handleSelect}
                currentWholesalerId={values.wholesalerID}
            />
        </>
    );
};

export default WholesalerField;
